/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/naming-convention */
import { Component, JSXElement, Show } from 'solid-js';
import { AlertType } from '@trimble/types';
import { BtnClasses, AlertRole } from '../../types/common';
import Button from '../Button/Button';

export type { AlertType };
export type AlertButtonColor = BtnClasses;

export interface AlertProps {
  // Content
  content?: JSXElement | string;
  children?: JSXElement | string;

  // Alert appearance
  type?: AlertType;

  // Icon support
  icon?: string;

  // Button support
  buttonText?: string;
  buttonColor?: AlertButtonColor;
  onButtonClick?(): void;

  // Accessibility
  title?: string;
  'aria-label'?: string;
  'data-cy'?: string;
  role?: AlertRole;

  // Additional classes
  class?: string;
}

const Alert: Component<AlertProps> = (props): JSXElement => {
  // Reactive getters for props
  const getContent = () => props.content || props.children || '';
  const getType = () => props.type || 'default';
  const getButtonText = () => props.buttonText || '';
  const getButtonColor = () => {
    // Default button colors based on alert type
    switch (getType()) {
      case 'info':
        return props.buttonColor || BtnClasses.PRIMARY;
      case 'warning':
        return props.buttonColor || BtnClasses.TERTIARY; // Using CTA equivalent
      case 'error':
        return props.buttonColor || BtnClasses.DANGER;
      default:
        return props.buttonColor || BtnClasses.DEFAULT;
    }
  };

  const hasButton = () => !!getButtonText();
  const hasIcon = () => !!props.icon;
  const hasContent = () => {
    const content = getContent();
    return typeof content === 'string' ? content.trim().length > 0 : !!content;
  };

  // Class name computation
  const getAlertClasses = () => {
    const classes = ['alert'];

    // Add type class if not default
    if (getType() !== 'default') {
      classes.push(getType());
    }

    // Add group class if has button
    if (hasButton()) {
      classes.push('group');
    }

    // Additional custom classes
    if (props.class) {
      classes.push(props.class);
    }

    return classes.join(' ');
  };

  // Event handlers - Pure callback pattern
  const handleButtonClick = () => {
    props.onButtonClick?.();
  };

  // Content rendering
  const renderContent = () => {
    if (!hasContent()) {
      return null;
    }

    const content = getContent();

    // Render icon button + content + action button layout
    if (hasButton()) {
      return (
        <div class="group-items">
          <div
            style={{
              display: 'flex',
              'align-items': 'center',
              'flex-grow': '1',
            }}
          >
            <Show when={hasIcon()}>
              <i
                class={`icon-font i16 ${props.icon!}`}
                aria-hidden="true"
                title={`${getType()} alert icon`}
              />
            </Show>
            <span class={`block flex-grow-1 ${hasIcon() ? 'ml-2' : ''} mr-2`}>
              {content}
            </span>
          </div>
          <Button
            content={getButtonText()}
            color={getButtonColor()}
            size="small"
            onClick={handleButtonClick}
            buttonType="button"
          />
        </div>
      );
    }

    // Render icon + content layout (no action button)
    if (hasIcon()) {
      return (
        <div style={{ display: 'flex', 'align-items': 'center' }}>
          <i
            class={`icon-font i16 ${props.icon!}`}
            aria-hidden="true"
            title={`${getType()} alert icon`}
          />
          <span class="block flex-grow-1 ml-2">{content}</span>
        </div>
      );
    }

    return content;
  };

  return (
    <div
      class={getAlertClasses()}
      title={props.title}
      aria-label={props['aria-label']}
      data-cy={props['data-cy'] || 'alert'}
      role={props.role || 'alert'}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      <Show when={hasContent()}>{renderContent()}</Show>
    </div>
  );
};

export default Alert;
